import * as React from "react";
import { Hero, PageHeader } from "@alight/ui";

export interface PageIntroProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  /** From the "Page banners" CMS block (`useBlock("banners")`). */
  image?: string;
  imageAlt?: string;
}

/**
 * The top of every inner page. With a photo (from the "Page banners" CMS
 * block) it renders the shared full-bleed `Hero` — parallax, legibility
 * scrim and fade-up entrance all come from that shared component, not
 * reimplemented here. Without one it falls back to the plain `PageHeader`
 * band, exactly as every inner page rendered before banners existed.
 */
export function PageIntro({ eyebrow, title, description, actions, image, imageAlt }: PageIntroProps) {
  if (image) {
    return (
      <Hero
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={actions}
        backgroundMedia={
          <img
            src={image}
            alt={imageAlt ?? ""}
            width={1800}
            height={900}
            loading="eager"
            decoding="async"
            // This is the LCP element on every inner page — hint the browser
            // to fetch it ahead of anything else discovered later in <head>.
            // React 18 (this repo's version) doesn't know the camelCase
            // `fetchPriority` prop yet, so spell it as the literal lowercase
            // HTML attribute — React passes unrecognised attributes straight
            // through to the DOM.
            // @ts-expect-error -- fetchpriority isn't in React 18's img prop types
            fetchpriority="high"
          />
        }
      />
    );
  }

  return <PageHeader eyebrow={eyebrow} title={title} description={description} actions={actions} />;
}
